import {
  useV2CreateResourceRateMutation,
  useV2UpdateResourceRateMutation,
  useV2DeleteResourceRateMutation,
} from "@generated";
import type { V2ResourceRateResource } from "@generated";
import type { ResourceRateFormValues } from "../types";

// Build the JSON:API `data` body. Wire keys are kebab-case (the generated type
// lies about `external_name`); cast covers that. `resource` is required on
// create and immutable after, so it is omitted on update. LOB linkage uses a
// null id to clear (mirrors pages/ResourcesV2/ResourcesV2.tsx).
export const buildResourceRateData = (
  values: ResourceRateFormValues,
  rateTableId: string,
  id?: string
): V2ResourceRateResource => {
  const relationships: any = {
    "rate-table": { data: { type: "rate-tables", id: rateTableId } },
  };
  if (!id) {
    relationships.resource = {
      data: { type: "resources", id: values.resourceId },
    };
  }
  relationships["line-of-business"] = {
    data: {
      type: "line-of-businesses",
      id: values.lobId || null,
    },
  };

  return {
    type: "resource-rates",
    ...(id ? { id } : {}),
    attributes: {
      "external-name": values.externalName,
      description: values.description,
      "hourly-rate": values.hourlyRate ?? 0,
      "hourly-cost": values.hourlyCost ?? 0,
      "expense-rate": values.expenseRate ?? 0,
      code: values.code,
    },
    relationships,
  } as unknown as V2ResourceRateResource;
};

export const useResourceRateMutations = (rateTableId: string) => {
  const [create, createState] = useV2CreateResourceRateMutation();
  const [update, updateState] = useV2UpdateResourceRateMutation();
  const [destroy, destroyState] = useV2DeleteResourceRateMutation();

  return {
    create: (values: ResourceRateFormValues) =>
      create({
        body: { data: buildResourceRateData(values, rateTableId) },
      }).unwrap(),

    update: (id: string, values: ResourceRateFormValues) =>
      update({
        id: id,
        body: { data: buildResourceRateData(values, rateTableId, id) },
      }).unwrap(),

    destroy: (id: string) => destroy({ id: id }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useResourceRateMutations;
